import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import languageCodes from './data/languages.json'
import de from './locales/de.json'
import en from './locales/en.json'
import es from './locales/es.json'
import fr from './locales/fr.json'
import it from './locales/it.json'
import nl from './locales/nl.json'
import zh from './locales/zh.json'

// The full list of routable languages - also read by scripts/generate-
// sitemap.mjs, so a language shows up in both the app and the sitemap/
// hreflang tags from one edit to that JSON file, no risk of drift between
// them. Not every entry here needs a locales/*.json yet (see `resources`
// below): a language with no catalogue simply falls back to English via
// `fallbackLng`, same as any missing key normally would.
export const SUPPORTED_LANGUAGES = languageCodes as unknown as readonly [
  'en',
  'nl',
  'fr',
  'de',
  'it',
  'zh',
  'es',
]
export type Language = (typeof SUPPORTED_LANGUAGES)[number]
export const DEFAULT_LANGUAGE: Language = 'en'

export function isSupportedLanguage(value: string): value is Language {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
}

const STORAGE_KEY = 'intraveo.lang'

/** The language the visitor last chose, if it is still valid. */
export function readStoredLanguage(): Language | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return value && isSupportedLanguage(value) ? value : null
  } catch {
    return null
  }
}

/** Remember the visitor's language choice for next time. */
export function storeLanguage(language: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, language)
  } catch {
    // private mode / storage disabled: not remembering is acceptable
  }
}

/** Stored choice first, then the browser preference, then English. */
export function initialLanguage(): Language {
  return readStoredLanguage() ?? detectLanguage()
}

/** Pick a supported language from the visitor's browser preferences, English as fallback. */
export function detectLanguage(): Language {
  const candidates =
    typeof navigator !== 'undefined'
      ? navigator.languages?.length
        ? navigator.languages
        : [navigator.language]
      : []
  for (const candidate of candidates) {
    const code = candidate.slice(0, 2).toLowerCase()
    if (isSupportedLanguage(code)) return code
  }
  return DEFAULT_LANGUAGE
}

// English plus Dutch, French, German, Chinese, Italian and Spanish
// catalogues exist. The German, French, Chinese, Italian and Spanish
// catalogues are unreviewed machine drafts, see src/locales/NOTES.md.
void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    nl: { translation: nl },
    fr: { translation: fr },
    de: { translation: de },
    it: { translation: it },
    zh: { translation: zh },
    es: { translation: es },
  },
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES,
  interpolation: { escapeValue: false },
})

export default i18n
